import React, { useState, useMemo } from "react";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import yaml from 'js-yaml';
import {
  ExclamationTriangleIcon,
  DocumentTextIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CalendarIcon,
  BuildingOfficeIcon,
  CurrencyDollarIcon,
} from "@heroicons/react/24/outline";

// Types
interface Recipient {
  name: string;
  role?: string;
  organisation?: string;
}

interface Sender {
  name: string;
  role?: string;
  organisation?: string;
}

interface Signer {
  name: string;
  title?: string;
}

interface Advisor {
  item: string;
  firm: string;
}

interface Contact {
  name: string;
  role?: string;
  email?: string;
  phone?: string;
}

interface DocumentMetadata {
  letter_date?: string;
  target_company?: string;
  valuation?: string;
  recipient_bank?: string;
  recipients?: Recipient[];
  senders?: Sender[];
}

interface ParagraphData {
  recipients?: Recipient[];
  bank?: string;
  closing_text?: string;
  signers?: Signer[];
  on_behalf?: string;
  title?: string;
  intro?: string;
  advisors?: Advisor[];
  contacts?: Contact[];
}

interface Paragraph {
  type?: string;
  data: string | ParagraphData;
  style?: string;
  section?: string;
  fixed?: boolean;
}

interface YamlDocumentData {
  metadata?: DocumentMetadata;
  paragraphs?: Paragraph[];
}

interface YamlDocumentPreviewProps {
  contentText: string;
}

interface MetadataFieldProps {
  icon: JSX.Element;
  label: string;
  value: string;
}

interface ParagraphItemProps {
  paragraph: Paragraph;
  index: number;
  isExpanded: boolean;
  onToggle: () => void;
}

interface TypeBadgeProps {
  type: string;
}

interface StyleBadgeProps {
  style: string;
}

interface ParagraphContentProps {
  paragraph: Paragraph;
}

/**
 * YamlDocumentPreview Component
 * Renders structured YAML documents with collapsible sections
 * Specifically designed for doc-drafter offer letters but extensible to other YAML formats
 */
export default function YamlDocumentPreview_Component({ contentText }: YamlDocumentPreviewProps): JSX.Element {
  const [metadataExpanded, setMetadataExpanded] = useState<boolean>(true);
  const [expandedParagraphs, setExpandedParagraphs] = useState<Set<number>>(new Set());

  // Parse YAML and handle errors
  const { parsedData, parseError } = useMemo(() => {
    try {
      const data = yaml.load(contentText) as YamlDocumentData;
      return { parsedData: data, parseError: null };
    } catch (error) {
      return { parsedData: null, parseError: (error as Error).message };
    }
  }, [contentText]);

  // Check if this YAML has the paragraphs structure we can preview
  const hasParagraphsStructure = parsedData && Array.isArray(parsedData.paragraphs);

  const toggleParagraph = (index: number): void => {
    const newExpanded = new Set(expandedParagraphs);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedParagraphs(newExpanded);
  };

  const toggleAll = (expand: boolean): void => {
    if (expand && parsedData?.paragraphs) {
      setExpandedParagraphs(new Set(parsedData.paragraphs.map((_, idx) => idx)));
    } else {
      setExpandedParagraphs(new Set());
    }
  };

  // Error state - YAML parsing failed
  if (parseError) {
    return (
      <div className="h-full overflow-auto p-6 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-start space-x-3">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-lg font-semibold text-red-900 mb-2">
                  Invalid YAML Format
                </h3>
                <p className="text-sm text-red-700 mb-3">
                  The YAML file could not be parsed. Please check the file format.
                </p>
                <pre className="text-xs text-red-600 bg-red-100 p-3 rounded font-mono overflow-x-auto">
                  {parseError}
                </pre>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // No paragraphs structure - fall back to raw YAML display
  if (!hasParagraphsStructure) {
    return (
      <div className="h-full overflow-auto p-6 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
            <div className="flex items-start space-x-2">
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-semibold text-yellow-900 mb-1">
                  No Structured Preview Available
                </h4>
                <p className="text-xs text-yellow-700">
                  This YAML file doesn't have the expected "paragraphs" structure for rich preview. Showing raw content below.
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <pre className="text-xs text-gray-700 font-mono leading-relaxed whitespace-pre-wrap">
              {contentText}
            </pre>
          </div>
        </div>
      </div>
    );
  }

  // Render structured document with collapsible sections
  return (
    <div className="h-full overflow-auto bg-gray-50">
      <div className="max-w-5xl mx-auto py-6 px-4">
        {/* Metadata Section - Collapsible */}
        {parsedData?.metadata && (
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm mb-4">
            <div
              onClick={() => setMetadataExpanded(!metadataExpanded)}
              className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors cursor-pointer"
            >
              <div className="flex items-center space-x-2">
                <DocumentTextIcon className="h-5 w-5 text-cvc-blue" />
                <h2 className="text-base font-semibold text-gray-900">Document Metadata</h2>
                <span className="text-xs text-gray-500">
                  ({Object.keys(parsedData.metadata).length} fields)
                </span>
              </div>
              {metadataExpanded ? (
                <ChevronDownIcon className="h-5 w-5 text-gray-400" />
              ) : (
                <ChevronRightIcon className="h-5 w-5 text-gray-400" />
              )}
            </div>

            {metadataExpanded && (
              <div className="px-4 pb-4 border-t border-gray-100">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  {parsedData.metadata.letter_date && (
                    <MetadataField
                      icon={<CalendarIcon className="h-5 w-5 text-gray-400" />}
                      label="Date"
                      value={parsedData.metadata.letter_date}
                    />
                  )}
                  {parsedData.metadata.target_company && (
                    <MetadataField
                      icon={<BuildingOfficeIcon className="h-5 w-5 text-gray-400" />}
                      label="Target Company"
                      value={parsedData.metadata.target_company}
                    />
                  )}
                  {parsedData.metadata.valuation && (
                    <MetadataField
                      icon={<CurrencyDollarIcon className="h-5 w-5 text-gray-400" />}
                      label="Valuation"
                      value={parsedData.metadata.valuation}
                    />
                  )}
                  {parsedData.metadata.recipient_bank && (
                    <MetadataField
                      icon={<BuildingOfficeIcon className="h-5 w-5 text-gray-400" />}
                      label="Recipient Bank"
                      value={parsedData.metadata.recipient_bank}
                    />
                  )}
                </div>

                {/* Recipients */}
                {parsedData.metadata.recipients && parsedData.metadata.recipients.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Recipients</div>
                    <div className="space-y-1">
                      {parsedData.metadata.recipients.map((recipient, idx) => (
                        <div key={idx} className="text-sm text-gray-700">
                          {recipient.name}{recipient.role && ` - ${recipient.role}`}
                          {recipient.organisation && ` (${recipient.organisation})`}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Senders */}
                {parsedData.metadata.senders && parsedData.metadata.senders.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Senders</div>
                    <div className="space-y-1">
                      {parsedData.metadata.senders.map((sender, idx) => (
                        <div key={idx} className="text-sm text-gray-700">
                          {sender.name}{sender.role && ` - ${sender.role}`}
                          {sender.organisation && ` (${sender.organisation})`}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Paragraphs Section */}
        {parsedData?.paragraphs && (
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <DocumentTextIcon className="h-5 w-5 text-blue-600" />
                <h2 className="text-base font-semibold text-gray-900">Document Paragraphs</h2>
                <span className="text-xs text-gray-500">
                  ({parsedData.paragraphs.length} paragraphs)
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => toggleAll(true)}
                  className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                >
                  Expand All
                </button>
                <span className="text-gray-300">|</span>
                <button
                  onClick={() => toggleAll(false)}
                  className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                >
                  Collapse All
                </button>
              </div>
            </div>

            <div className="divide-y divide-gray-100">
              {parsedData.paragraphs.map((paragraph, index) => (
                <ParagraphItem
                  key={index}
                  paragraph={paragraph}
                  index={index}
                  isExpanded={expandedParagraphs.has(index)}
                  onToggle={() => toggleParagraph(index)}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Metadata Field Component
 */
function MetadataField({ icon, label, value }: MetadataFieldProps): JSX.Element {
  return (
    <div className="flex items-start space-x-3">
      {icon}
      <div>
        <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</div>
        <div className="text-sm text-gray-900 mt-0.5">{value}</div>
      </div>
    </div>
  );
}

/**
 * Collapsible Paragraph Item
 */
function ParagraphItem({ paragraph, index, isExpanded, onToggle }: ParagraphItemProps): JSX.Element {
  const { type, data, style, section, fixed } = paragraph;
  const paragraphType = type || 'markdown';

  // Get preview text (first 100 chars)
  const getPreviewText = (): string => {
    if (paragraphType === 'markdown') {
      const text = typeof data === 'string' ? data : String(data || '');
      return text.length > 100 ? text.substring(0, 100) + '...' : text;
    }
    if (paragraphType === 'recipients') {
      const pData = data as ParagraphData;
      return pData.recipients?.map(r => r.name).join(', ') || 'Recipients';
    }
    if (paragraphType === 'signatures') {
      const pData = data as ParagraphData;
      return pData.signers?.map(s => s.name).join(', ') || 'Signatures';
    }
    if (paragraphType === 'advisors-table') {
      const pData = data as ParagraphData;
      return pData.title || 'Advisors Table';
    }
    if (paragraphType === 'contacts-table') {
      const pData = data as ParagraphData;
      return pData.title || 'Contacts Table';
    }
    return `${paragraphType} content`;
  };

  return (
    <div className="hover:bg-gray-50 transition-colors">
      <div
        onClick={onToggle}
        className="w-full flex items-start justify-between p-4 cursor-pointer"
      >
        <div className="flex items-start space-x-3 flex-1 min-w-0">
          {isExpanded ? (
            <ChevronDownIcon className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
          ) : (
            <ChevronRightIcon className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2 mb-1">
              <span className="text-xs font-medium text-gray-500">#{index}</span>
              <TypeBadge type={paragraphType} />
              {style && <StyleBadge style={style} />}
              {section && <span className="text-xs text-gray-400">• {section}</span>}
              {fixed && (
                <span className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">
                  fixed
                </span>
              )}
            </div>
            {!isExpanded && (
              <div className="text-sm text-gray-600 truncate">
                {getPreviewText()}
              </div>
            )}
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="px-4 pb-4 pl-12">
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <ParagraphContent paragraph={paragraph} />
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Type Badge Component
 */
function TypeBadge({ type }: TypeBadgeProps): JSX.Element {
  const colors: Record<string, string> = {
    markdown: 'bg-blue-100 text-blue-700',
    recipients: 'bg-purple-100 text-purple-700',
    signatures: 'bg-green-100 text-green-700',
    'advisors-table': 'bg-orange-100 text-orange-700',
    'contacts-table': 'bg-orange-100 text-orange-700',
  };

  return (
    <span className={`text-xs px-2 py-0.5 rounded font-medium ${colors[type] || 'bg-gray-100 text-gray-700'}`}>
      {type}
    </span>
  );
}

/**
 * Style Badge Component
 */
function StyleBadge({ style }: StyleBadgeProps): JSX.Element {
  return (
    <span className="text-xs px-2 py-0.5 rounded font-medium bg-indigo-100 text-indigo-700">
      {style}
    </span>
  );
}

/**
 * Paragraph Content Renderer
 */
function ParagraphContent({ paragraph }: ParagraphContentProps): JSX.Element {
  const { type, data } = paragraph;

  // Type: recipients
  if (type === 'recipients') {
    const pData = data as ParagraphData;
    return (
      <div className="space-y-2">
        {pData.recipients && pData.recipients.map((recipient, idx) => (
          <div key={idx} className="text-gray-900">
            <div className="font-medium">{recipient.name}</div>
            {recipient.role && <div className="text-sm text-gray-600">{recipient.role}</div>}
          </div>
        ))}
        {pData.bank && (
          <div className="mt-2 text-gray-900">{pData.bank}</div>
        )}
      </div>
    );
  }

  // Type: signatures
  if (type === 'signatures') {
    const pData = data as ParagraphData;
    return (
      <div className="space-y-4">
        {pData.closing_text && (
          <div className="text-gray-700 prose prose-sm max-w-none">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{pData.closing_text}</ReactMarkdown>
          </div>
        )}
        <div className="space-y-3 pt-3 border-t border-gray-200">
          {pData.signers && pData.signers.map((signer, idx) => (
            <div key={idx} className="space-y-0.5">
              <div className="font-medium text-gray-900">{signer.name}</div>
              <div className="text-sm text-gray-600">{signer.title}</div>
            </div>
          ))}
          {pData.on_behalf && (
            <div className="text-sm text-gray-600 italic mt-2">On behalf of {pData.on_behalf}</div>
          )}
        </div>
      </div>
    );
  }

  // Type: advisors-table
  if (type === 'advisors-table') {
    const pData = data as ParagraphData;
    return (
      <div className="space-y-3">
        {pData.title && (
          <h4 className="font-semibold text-gray-900">{pData.title}</h4>
        )}
        {pData.intro && (
          <p className="text-sm text-gray-700">{pData.intro}</p>
        )}
        {pData.advisors && pData.advisors.length > 0 && (
          <div className="overflow-hidden border border-gray-300 rounded">
            <table className="min-w-full divide-y divide-gray-300 text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase">Role</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase">Firm</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {pData.advisors.map((advisor, idx) => (
                  <tr key={idx}>
                    <td className="px-3 py-2 text-gray-900">{advisor.item}</td>
                    <td className="px-3 py-2 text-gray-700">{advisor.firm}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  // Type: contacts-table
  if (type === 'contacts-table') {
    const pData = data as ParagraphData;
    return (
      <div className="space-y-3">
        {pData.title && (
          <h4 className="font-semibold text-gray-900">{pData.title}</h4>
        )}
        {pData.intro && (
          <p className="text-sm text-gray-700">{pData.intro}</p>
        )}
        {pData.contacts && pData.contacts.length > 0 && (
          <div className="overflow-hidden border border-gray-300 rounded">
            <table className="min-w-full divide-y divide-gray-300 text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase">Name</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase">Role</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase">Email</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase">Phone</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {pData.contacts.map((contact, idx) => (
                  <tr key={idx}>
                    <td className="px-3 py-2 font-medium text-gray-900">{contact.name}</td>
                    <td className="px-3 py-2 text-gray-700">{contact.role}</td>
                    <td className="px-3 py-2 text-blue-600">
                      <a href={`mailto:${contact.email}`} className="hover:underline">
                        {contact.email}
                      </a>
                    </td>
                    <td className="px-3 py-2 text-gray-700">{contact.phone}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  // Type: markdown (default)
  const paragraphData = typeof data === 'string' ? data : String(data || '');

  return (
    <div className="prose prose-sm max-w-none">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({children}) => <h1 className="text-xl font-bold text-gray-900 mt-4 mb-2 first:mt-0">{children}</h1>,
          h2: ({children}) => <h2 className="text-lg font-bold text-gray-900 mt-3 mb-2">{children}</h2>,
          h3: ({children}) => <h3 className="text-base font-bold text-gray-900 mt-2 mb-1">{children}</h3>,
          p: ({children}) => <p className="text-gray-700 mb-2 leading-relaxed">{children}</p>,
          ul: ({children}) => <ul className="list-disc list-inside mb-2 text-gray-700">{children}</ul>,
          ol: ({children}) => <ol className="list-decimal list-inside mb-2 text-gray-700">{children}</ol>,
          strong: ({children}) => <strong className="font-semibold text-gray-900">{children}</strong>,
          em: ({children}) => <em className="italic text-gray-700">{children}</em>,
          code: ({children, className}) => {
            const isInline = !className;
            return isInline ?
              <code className="bg-gray-200 px-1 py-0.5 rounded text-xs font-mono text-gray-800">{children}</code> :
              <code className="block bg-gray-200 p-2 rounded text-xs font-mono text-gray-800 overflow-x-auto">{children}</code>;
          },
        }}
      >
        {paragraphData}
      </ReactMarkdown>
    </div>
  );
}
